import * as React from "react";
import { CheckCircle2 } from "lucide-react";

import { ApiError, getMyInterest, submitInterest } from "@/api/client";
import { PageTitle } from "@/components/PageTitle";
import type { ApprovalStatus, PreviewInterest } from "@/api/types";
import { SignedInHeader } from "@/auth/SignedInHeader";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ComicPanel } from "@/components/comic";
import { useProfile } from "@/features/profile/ProfileContext";

const MAX_LEN = 280;

/**
 * Shown to a signed-in user whose account isn't approved. The user holds a valid
 * token but every protected API is refused by the backend's
 * ApprovalGateInterceptor, so this screen is the whole experience until that
 * changes.
 *
 * <p>Two ways to land here, and the copy has to serve both. The common one now
 * is DISABLED: an admin revoked an account that was working. The other is
 * PENDING, which a new sign-up only gets if the approval gate was switched back
 * on (APP_APPROVAL_GATE_ENABLED=true) — by default a new account is approved on
 * arrival and never sees this page at all. Anything that promises approval is
 * coming must therefore stay on the PENDING branch: a disabled user is not in a
 * queue, and telling them to sit tight for an approval that no process will
 * produce is worse than telling them nothing.</p>
 *
 * <p>The note box is the one write a gated user is allowed
 * (POST /preview-interest is allowlisted). It is addressed FROM the team, not
 * from an agent: a waitlisted user does not have an agent yet. Provisioning
 * deliberately creates app_user and nothing else until approval, so the older
 * "your agent will carry the message for you" framing described something that
 * did not exist for the person reading it.</p>
 */
export function WaitlistPage({ status }: { status: ApprovalStatus }) {
  const disabled = status === "disabled";
  const [thread, setThread] = React.useState<PreviewInterest[]>([]);
  const [draft, setDraft] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [justSent, setJustSent] = React.useState(false);
  const { refresh } = useProfile();

  // Auto-detect approval: quietly re-fetch the profile on an interval and on tab
  // focus. The moment an admin flips the account to APPROVED, the app's approval
  // gate re-evaluates and swaps this page for the real app, with no manual
  // sign-out / sign-in. Paused accounts skip the poll (nothing to wait for).
  React.useEffect(() => {
    if (disabled) return;
    let cancelled = false;
    const poll = () => {
      if (!cancelled) void refresh();
    };
    const id = window.setInterval(poll, 15_000);
    window.addEventListener("focus", poll);
    return () => {
      cancelled = true;
      window.clearInterval(id);
      window.removeEventListener("focus", poll);
    };
  }, [refresh, disabled]);

  React.useEffect(() => {
    let cancelled = false;
    getMyInterest()
      .then((t) => {
        if (!cancelled) setThread(t);
      })
      .catch(() => {
        /* non-fatal: the compose box still works */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function send(e: React.FormEvent) {
    e.preventDefault();
    const message = draft.trim();
    if (!message || sending) return;
    setSending(true);
    setError(null);
    setJustSent(false);
    try {
      const updated = await submitInterest(message);
      setThread(updated);
      setDraft("");
      setJustSent(true);
    } catch (err) {
      setError(describeError(err));
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="theme-primary-brand-blue mx-auto max-w-2xl space-y-6 px-4 py-8">
      <header className="flex items-start justify-between gap-3">
        <PageTitle>{disabled ? "Access paused" : "Welcome to the preview"}</PageTitle>
        <SignedInHeader />
      </header>

      <ComicPanel title={disabled ? "Account paused" : "You're early"}>
        {disabled ? (
          <p className="text-sm text-foreground">
            Your preview access is currently paused. Send the team a note below
            and we&apos;ll take another look.
          </p>
        ) : (
          <p className="text-sm text-foreground">
            The product is opening up gradually. Tell us a little about what
            you&apos;re hoping to do and we&apos;ll fast-track your access.
          </p>
        )}
      </ComicPanel>

      <section className="comic-shadow relative space-y-4 rounded-xl border border-border bg-card p-5">
        <form onSubmit={send} className="space-y-2">
          <Label htmlFor="interest-note">
            {disabled ? "Send the team a note" : "Tell the team about your use case"}
          </Label>
          <textarea
            id="interest-note"
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              setJustSent(false);
            }}
            maxLength={MAX_LEN}
            rows={3}
            placeholder={
              disabled
                ? "e.g. I still need my booking links for the clinic rota."
                : "e.g. I run a physio practice and want a rolling link for patient bookings."
            }
            disabled={sending}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          />
          <div className="flex items-center justify-between gap-3">
            <span
              className={`text-xs ${
                draft.length > MAX_LEN - 40 ? "text-brand-coral" : "text-muted-foreground"
              }`}
            >
              {draft.length}/{MAX_LEN}
            </span>
            <Button type="submit" disabled={sending || !draft.trim()}>
              {sending ? "Sending…" : "Send note"}
            </Button>
          </div>
        </form>

        {error && (
          <p role="alert" className="text-sm font-medium text-brand-coral">
            {error}
          </p>
        )}
        {justSent && !error && (
          <div className="flex items-start gap-2 rounded-xl border border-brand-teal/50 bg-brand-teal/10 p-3">
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-brand-teal" aria-hidden="true" />
            <p className="text-sm text-foreground">
              {disabled
                ? "Sent. The team will reply by email."
                : "Sent. We'll email you the moment you're approved."}
            </p>
          </div>
        )}

        {thread.length > 0 && (
          <div className="border-t border-border pt-4">
            <h2 className="font-display text-xs font-bold uppercase tracking-wide text-foreground">
              What you&apos;ve sent
            </h2>
            <ul className="mt-2 space-y-2">
              {thread.map((note) => (
                <li
                  key={note.id}
                  className="rounded-md border border-border bg-muted px-3 py-2 text-sm text-foreground"
                >
                  <p className="leading-snug">{note.message}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {formatWhen(note.createdAt)}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {/* Only the PENDING branch polls (see the effect above), so only it may promise
          that the page will let you in by itself. */}
      {!disabled && (
        <p className="text-center text-xs text-muted-foreground">
          This page checks automatically. The moment you&apos;re approved, you&apos;ll
          come straight in.
        </p>
      )}
    </div>
  );
}

function describeError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 429) {
      return "You've sent a few notes already. The team will be in touch soon.";
    }
    if (err.status === 400) {
      return "That note looks empty or too long. Keep it under 280 characters.";
    }
    return `Couldn't send: ${err.status}`;
  }
  return err instanceof Error ? err.message : "Couldn't send your note.";
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
}
